import { palette } from 'src/styles/colors';
import { layout } from 'src/styles/layout';
import styled, { Keyframes, css, keyframes } from 'styled-components';

export const MeetMightyProSliderContainer = styled.div`
  position: relative;
  max-width: 750px;
  margin: 0 auto;
`;

interface ArrowProps {
  isMobileOnly?: boolean;
}

const arrowStyles = css<ArrowProps>`
  width: 60px;
  height: 60px;
  top: 50%;
  transform: translateY(-50%);
  position: absolute;
  cursor: pointer;
  z-index: 5;

  ${({ isMobileOnly }) =>
    isMobileOnly
      ? css`
          @media (max-width: ${layout.subxlg}) {
            display: none;
          }
        `
      : ''}

  @media (max-width: ${layout.subxlg}) {
    position: relative;
    transform: unset;
    top: unset;
  }
`;

export const ArrowLeft = styled.div<ArrowProps>`
  ${arrowStyles}
  left: 0;
`;

export const ArrowRight = styled.div<ArrowProps>`
  ${arrowStyles}
  right: 0;
`;

export const ArrowsContainerMobile = styled.div`
  display: none;
  position: relative;
  flex-wrap: nowrap;
  justify-content: center;
  column-gap: 1.5em;
  margin-top: 6rem;

  @media (max-width: ${layout.subxlg}) {
    display: flex;
  }

  @media (max-width: ${layout.md}) {
    margin-top: 0rem;
  }
`;

interface CardsContainerProps {
  fullHeight?: boolean;
}

export const CardsContainer = styled.div<CardsContainerProps>`
  position: relative;
  width: 100%;
  height: 520px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;

  @media (max-width: ${layout.xlg}) {
    height: 450px;
  }

  @media (max-width: ${layout.subxlg}) {
    height: min(380px, 69vh);
    margin-top: 2rem;
  }

  @media (max-width: ${layout.md}) {
    height: ${({ fullHeight }) => (fullHeight ? '74vh' : 'min(860px, 74vh)')};
  }

  @media (max-width: ${layout.xs}) {
    max-height: 60vh;
    min-height: 400px;
  }
`;

const currentCardStyles = css`
  transform: rotate(0) translateX(0);
  top: 0;
  opacity: 1;
  z-index: 4;
`;

const hiddenCardStyles = css`
  ${currentCardStyles}
  opacity: 0;
  z-index: 1;
`;

const secondCardStyles = css`
  transform: rotate(-5deg) translateX(-3rem);
  top: 3rem;
  opacity: 1;
  z-index: 3;
`;

const thirdCardStyles = css`
  transform: rotate(5deg) translateX(3rem);
  top: 3rem;
  opacity: 1;
  z-index: 2;
`;

const secondCardStylesMobile = css`
  ${secondCardStyles}
  transform: rotate(-2deg) translateX(-1rem);
`;

const thirdCardStylesMobile = css`
  ${thirdCardStyles}
  transform: rotate(2deg) translateX(1rem);
`;

const PREDEFINED_STYLES = {
  center: currentCardStyles,
  left: secondCardStyles,
  right: thirdCardStyles,
  hidden: hiddenCardStyles,
};

const PREDEFINED_STYLES_MOBILE = {
  ...PREDEFINED_STYLES,
  left: secondCardStylesMobile,
  right: thirdCardStylesMobile,
};

type PredefinedStyles = keyof typeof PREDEFINED_STYLES;

const moveOutCard = keyframes`
  0% {
    ${currentCardStyles}
  }
  50% {
    z-index: 1;
    opacity: 1;
    transform: rotate(25deg) translateX(120%) translateY(-40%);
  }
  95% {
    opacity: 1;
  }
  100% {
    ${hiddenCardStyles}
  }
`;

const moveBackCard = keyframes`
  0% {
    ${hiddenCardStyles}
  }
  5% {
    opacity: 1;
  }
  50% {
    z-index: 1;
    opacity: 1;
    transform: rotate(25deg) translateX(120%) translateY(-40%);
  }
  100% {
    ${currentCardStyles}
  }
`;

const getTransitionCardKeyframe = (from: PredefinedStyles, to: PredefinedStyles, mobile = false): Keyframes => {
  const predefinedStyles = mobile ? PREDEFINED_STYLES_MOBILE : PREDEFINED_STYLES;

  return keyframes`
    0% {
      ${predefinedStyles[from]}
    }
    100% {
      ${predefinedStyles[to]}
    }
  `;
};

const ANIMATION_TIME = 500;
const ANIMATION_TIME_FOR_FIRST_AND_LAST = ANIMATION_TIME + 400;

export const Card = styled.div`
  transition: all 500ms ease-out;
  position: absolute;
  width: 350px;
  height: 420px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  border: 1px solid ${palette.cream};
  border-radius: 1rem;
  ${currentCardStyles}

  .gatsby-image-wrapper {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 1rem;
  }

  &.from-center-to-bottom {
    animation: ${moveOutCard} ${ANIMATION_TIME_FOR_FIRST_AND_LAST}ms ease-out;
    ${hiddenCardStyles}
  }

  &.from-left-to-center {
    animation: ${getTransitionCardKeyframe('left', 'center')} ${ANIMATION_TIME}ms ease-out;
    ${currentCardStyles}
  }

  &.from-right-to-left {
    animation: ${getTransitionCardKeyframe('right', 'left')} ${ANIMATION_TIME}ms ease-out;
    ${secondCardStyles}
  }

  &.third-card-appear {
    animation: ${getTransitionCardKeyframe('hidden', 'right')} ${ANIMATION_TIME}ms ease-out;
    ${thirdCardStyles}
  }

  &.from-bottom-to-center {
    animation: ${moveBackCard} ${ANIMATION_TIME_FOR_FIRST_AND_LAST}ms ease-out;
    ${currentCardStyles}
  }

  &.from-center-to-left {
    animation: ${getTransitionCardKeyframe('center', 'left')} ${ANIMATION_TIME}ms ease-out;
    ${secondCardStyles}
  }

  &.from-left-to-right {
    animation: ${getTransitionCardKeyframe('left', 'right')} ${ANIMATION_TIME}ms ease-out;
    ${thirdCardStyles}
  }

  &.hidden {
    ${hiddenCardStyles}
  }

  @media (max-width: ${layout.xlg}) {
    width: 290px;
    height: 360px;
  }

  @media (max-width: ${layout.md}) {
    width: min(640px, 80vw);
    height: min(760px, 60vh);

    &.from-left-to-center {
      animation: ${getTransitionCardKeyframe('left', 'center', true)} ${ANIMATION_TIME}ms ease-out;
      ${currentCardStyles}
    }

    &.from-right-to-left {
      animation: ${getTransitionCardKeyframe('right', 'left', true)} ${ANIMATION_TIME}ms ease-out;
      ${secondCardStylesMobile}
    }

    &.third-card-appear {
      animation: ${getTransitionCardKeyframe('hidden', 'right', true)} ${ANIMATION_TIME}ms ease-out;
      ${thirdCardStylesMobile}
    }

    &.from-center-to-left {
      animation: ${getTransitionCardKeyframe('center', 'left', true)} ${ANIMATION_TIME}ms ease-out;
      ${secondCardStylesMobile}
    }

    &.from-left-to-right {
      animation: ${getTransitionCardKeyframe('left', 'right', true)} ${ANIMATION_TIME}ms ease-out;
      ${thirdCardStylesMobile}
    }
  }

  @media (max-width: ${layout.xs}) {
    max-height: 50vh;
    min-height: 300px;
  }
`;

export const CardTextBlock = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  justify-content: center;
  flex-wrap: wrap;
  padding: 0 1rem;

  h1 {
    max-width: 410px;
    padding-bottom: 1rem;
  }

  p {
    max-width: 450px;
  }

  @media (max-width: ${layout.md}) {
    padding: 2rem 1rem;
  }
`;
